import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { routerHistory } from "../redux/redux-config";
import actions from "../actions/actions";
import "../styles/create-group-dialog.scss";

export function CreateGroupDialog({ selectedRows, onClose, onSuccess }) {
  const [groupName, setGroupName] = useState("");
  const [nameError, setNameError] = useState("");
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form"); // "form" | "success"
  const [createdGroupId, setCreatedGroupId] = useState(null);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  const userIds = selectedRows.map((r) => r.userId).filter((id) => id != null);

  useEffect(() => {
    if (view === "form") inputRef.current?.focus();
  }, [view]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  function handleBackdropClick(e) {
    if (dialogRef.current && !dialogRef.current.contains(e.target)) {
      onClose();
    }
  }

  function handleNameChange(e) {
    setGroupName(e.target.value);
    if (nameError) setNameError("");
  }

  async function handleCreate() {
    const name = groupName.trim();
    if (!name) {
      setNameError("Group name is required");
      inputRef.current?.focus();
      return;
    }

    setSaving(true);

    try {
      const saveResult = await actions.groups.save({
        id: 0,
        name,
        groupType: 5,
      });

      if (!saveResult?.success) {
        setNameError("Failed to create group. Please try again.");
        setSaving(false);
        return;
      }

      const groupId = saveResult.id ?? saveResult.Id;

      if (userIds.length > 0) {
        const addResult = await actions.people.addPeopleToGroup(
          groupId,
          userIds,
        );

        if (!addResult?.success) {
          setNameError("Group created but members could not be added.");
          setSaving(false);
          return;
        }
      }

      setCreatedGroupId(groupId);
      setView("success");
    } catch {
      setNameError("An unexpected error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleViewGroup() {
    onSuccess();
    const details = await actions.groups.getAllGroupMembers(createdGroupId);
    routerHistory.push(`/group-members/${createdGroupId}`, {
      groupDetails: details,
      groupName: groupName,
    });
  }

  const content =
    view === "success" ? (
      <div
        className="cgd-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cgd-title"
        onClick={handleBackdropClick}
      >
        <div className="cgd-dialog" ref={dialogRef}>
          <div className="cgd-success">
            <div className="cgd-success-icon">
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="28" cy="28" r="28" fill="#EBF6EE" />
                <path
                  d="M18 28.5L24.5 35L38 21"
                  stroke="#3CA455"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="cgd-success-title">Group Created!</h2>
            <p className="cgd-success-body">
              <strong>{groupName}</strong> was created with {userIds.length}{" "}
              member{userIds.length !== 1 ? "s" : ""}.
            </p>
            <div className="cgd-actions cgd-actions--center">
              <button className="cgd-btn cgd-btn--outline" onClick={onSuccess}>
                Back to Results
              </button>
              <button
                className="cgd-btn cgd-btn--primary"
                onClick={handleViewGroup}
              >
                View Group
              </button>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div
        className="cgd-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cgd-title"
        onClick={handleBackdropClick}
      >
        <div className="cgd-dialog" ref={dialogRef}>
          <button
            className="cgd-close"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 2L14 14M14 2L2 14"
                stroke="#6B6B6B"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <h2 className="cgd-title" id="cgd-title">
            Create Group
          </h2>
          <p className="cgd-subtitle">
            {userIds.length} recipient{userIds.length !== 1 ? "s" : ""} will be
            added to the new group.
          </p>

          <div className={`cgd-field${nameError ? " cgd-field--error" : ""}`}>
            <label className="cgd-label" htmlFor="cgd-group-name">
              Group Name <span className="cgd-required">*</span>
            </label>
            <input
              id="cgd-group-name"
              ref={inputRef}
              className="cgd-input"
              type="text"
              value={groupName}
              onChange={handleNameChange}
              placeholder="Enter group name"
              maxLength={255}
              disabled={saving}
              onKeyDown={(e) => e.key === "Enter" && !saving && handleCreate()}
            />
            {nameError && (
              <span className="cgd-error-msg" role="alert">
                {nameError}
              </span>
            )}
          </div>

          <div className="cgd-actions">
            <button
              className="cgd-btn cgd-btn--outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="cgd-btn cgd-btn--primary"
              onClick={handleCreate}
              disabled={saving}
            >
              {saving ? (
                <span className="cgd-spinner" aria-hidden="true" />
              ) : null}
              {saving ? "Creating..." : "Create Group"}
            </button>
          </div>
        </div>
      </div>
    );

  return ReactDOM.createPortal(content, document.body);
}
